/**
 * GlobalRequestQueue – Hàng đợi có giới hạn concurrency
 *
 * Thay vì chạy tuần tự từng cái (concurrency=1) hoặc tất cả cùng lúc,
 * queue này chạy tối đa `concurrency` tasks song song cùng một lúc.
 *
 * Ví dụ concurrency=5, có 100 tasks:
 *   Slot [1,2,3,4,5] chạy → cái nào xong trước → slot đó nhận task tiếp theo
 *   → Luôn có đúng 5 tasks đang chạy cho đến khi hết hàng đợi
 *
 * Hoạt động:
 *  - Singleton: 1 instance dùng chung cho toàn bộ app
 *  - enqueue(fn, signal): nhận async function + AbortSignal, trả về Promise
 *  - Tasks bị abort (unmount) sẽ bị skip ngay, không chiếm slot
 *  - Có thể đổi concurrency bất cứ lúc nào qua setConcurrency(n)
 */
class GlobalRequestQueue {
  /**
   * @param {number} concurrency - Số task chạy song song tối đa (mặc định 5)
   */
  constructor(concurrency = 5) {
    this._queue       = [];          // Danh sách tasks đang chờ
    this._running     = 0;           // Số tasks đang chạy hiện tại
    this._concurrency = concurrency; // Giới hạn concurrency
  }

  /**
   * Thay đổi concurrency trong runtime
   * Nếu tăng lên, sẽ kích hoạt thêm slots ngay lập tức
   * @param {number} n
   */
  setConcurrency(n) {
    this._concurrency = Math.max(1, n);
    // Kick thêm slots nếu vừa tăng concurrency
    this._fill();
  }

  /**
   * Thêm 1 async task vào hàng đợi
   * @param {() => Promise<any>} fn     - Hàm async cần thực thi
   * @param {AbortSignal}        signal - Tín hiệu hủy từ AbortController
   * @returns {Promise<any>}
   */
  enqueue(fn, signal) {
    return new Promise((resolve, reject) => {
      this._queue.push({ fn, signal, resolve, reject });
      this._fill(); // Thử lấp đầy các slot còn trống
    });
  }

  /**
   * Lấp đầy các slot còn trống cho đến khi đạt concurrency hoặc hết queue
   * Mỗi lần 1 task xong, _fill() được gọi lại để nhận task tiếp theo
   */
  _fill() {
    while (this._running < this._concurrency && this._queue.length > 0) {
      this._runNext();
    }
  }

  async _runNext() {
    const { fn, signal, resolve, reject } = this._queue.shift();
    this._running++;

    try {
      // Component đã unmount trước khi đến lượt → skip, trả undefined
      if (signal?.aborted) {
        resolve(undefined);
        return;
      }
      const result = await fn();
      resolve(result);
    } catch (err) {
      reject(err);
    } finally {
      this._running--;
      // Slot vừa giải phóng → thử nhận task tiếp theo
      this._fill();
    }
  }

  /** Tiện ích debug: xem trạng thái queue */
  getStatus() {
    return {
      queued:      this._queue.length,
      running:     this._running,
      concurrency: this._concurrency,
    };
  }
}

// Singleton – concurrency mặc định là 5
// Có thể đổi bất cứ lúc nào: counterQueue.setConcurrency(10)
export const counterQueue = new GlobalRequestQueue(5);